import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { MdfDraftDetail } from "./types";

// DELETE /api/portal/mdf/attachments/:id — partner removes an attachment
// from a draft. Optimistic remove (mirror bucket 3 deal-comment pattern):
// drop the row from the cached draft list immediately, rollback on error.

type Context = {
  previous: MdfDraftDetail | undefined;
  key: ReturnType<typeof queryKeys.mdf.draft>;
};

export function useRemoveMdfAttachment(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiClientError, { id: string }, Context>({
    mutationFn: async ({ id }) => {
      await apiFetch<void>(`/portal/mdf/attachments/${id}`, {
        method: "DELETE",
      });
    },
    onMutate: async ({ id }) => {
      const key = queryKeys.mdf.draft(requestId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<MdfDraftDetail>(key);
      if (previous) {
        queryClient.setQueryData<MdfDraftDetail>(key, {
          ...previous,
          attachments: previous.attachments.filter((a) => a.id !== id),
        });
      }
      return { previous, key };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(err.message ?? "Failed to remove attachment");
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.mdf.draft(requestId),
      });
    },
  });
}
